import styled from "@emotion/styled";
import { Mono, Badge } from "../../components/ui";
import { theme } from "../../lib/theme";

export const Field = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

export const FieldLabel = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${theme.spacing.xs};
`;

export const FieldValue = styled(Mono)`
  font-size: ${theme.fontSize.sm};
  word-break: break-all;
`;

export const SlotLabelRow = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: ${theme.spacing.xs};
`;

export const HeatmapBar = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  height: 20px;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

export const HeatmapCell = styled.div<{ intensity: number; isFocal?: boolean }>`
  flex: 1;
  background: ${(p) =>
    p.isFocal
      ? theme.colors.warning
      : p.intensity > 0
        ? `rgba(88, 101, 242, ${Math.min(0.2 + p.intensity * 0.8, 1)})`
        : theme.colors.bgCard};
  border-right: 1px solid ${theme.colors.bg};

  &:last-child {
    border-right: none;
  }
`;

export const NearbyList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
`;

export const SlotRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border};
  }

  @media (max-width: 768px) {
    gap: ${theme.spacing.xs};
  }
`;

export const SlotEntry = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border};
  }
`;

export const WriteBadge = styled(Badge)`
  flex-shrink: 0;
  width: 56px;
  text-align: center;

  @media (max-width: 768px) {
    width: 36px;
    font-size: 10px;
  }
`;

export const CalldataCell = styled.td`
  padding: ${theme.spacing.xs} ${theme.spacing.sm} !important;
  background: ${theme.colors.bg};
`;

export const CalldataToggle = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  font-size: ${theme.fontSize.xs};
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

export const FeatureRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: ${theme.spacing.xs} 0;
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border};
  }
`;

export const FeatureName = styled.span`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
  margin-right: ${theme.spacing.sm};
`;

export const FeatureValue = styled(Mono)`
  font-size: ${theme.fontSize.sm};
`;

export const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.text};
  font-size: ${theme.fontSize.lg};
  font-weight: 600;
  margin-bottom: ${theme.spacing.md};

  &:hover {
    color: ${theme.colors.primary};
  }
`;

export const CollapsibleArrow = styled.span<{ open: boolean }>`
  display: inline-block;
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
  transition: transform 0.15s;
  transform: rotate(${(p) => (p.open ? "90deg" : "0deg")});
`;

export const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${theme.colors.border};
  margin-top: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
`;

export const Tab = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: none;
  border: none;
  border-bottom: 2px solid
    ${(p) => (p.active ? theme.colors.primary : "transparent")};
  margin-bottom: -2px;
  font-size: ${theme.fontSize.sm};
  font-weight: 600;
  color: ${(p) => (p.active ? theme.colors.primary : theme.colors.textMuted)};
  cursor: pointer;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

export const TopColumns = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: stretch;

  @media (max-width: 1400px) {
    flex-direction: column;
  }
`;

export const TopColumnLeft = styled.div`
  flex: 3 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const TopColumnRight = styled.div`
  flex: 7 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 1400px) {
    width: 100%;
  }
`;

export const FingerprintInner = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: stretch;
  flex: 1;
  min-height: 0;

  @media (max-width: 1400px) {
    flex-direction: column;
  }
`;

export const FingerprintChartCol = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const FingerprintValuesCol = styled.div<{ collapsed?: boolean }>`
  flex: 0 0 ${(p) => (p.collapsed ? "0px" : "35%")};
  min-width: 0;
  max-height: 400px;
  overflow: ${(p) => (p.collapsed ? "hidden" : "auto")};
  opacity: ${(p) => (p.collapsed ? 0 : 1)};
  transition:
    flex-basis 0.2s,
    opacity 0.2s;

  @media (max-width: 1400px) {
    display: none;
  }
`;

export const ValuesToggle = styled.button<{ collapsed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-end;
  background: ${theme.colors.bg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 4px 10px;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
  font-weight: 500;
  line-height: 1;
  margin-bottom: ${theme.spacing.xs};
  &:hover {
    color: ${theme.colors.text};
    background: ${theme.colors.bgHover};
    border-color: ${theme.colors.primary};
  }

  @media (max-width: 1400px) {
    display: none;
  }
`;

export const FingerprintValuesMobile = styled.div`
  display: none;

  @media (max-width: 1400px) {
    display: block;
  }
`;

export const SimilarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.sm};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const SimilarTxCard = styled(Card)`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
`;

export const SimilarTxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  font-size: ${theme.fontSize.xs};
`;

import { Card } from "../../components/ui";

export const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

export const SmallMono = styled(Mono)`
  font-size: 10px;
`;

export const ScrollableFields = styled.div`
  max-height: 160px;
  overflow-y: auto;
`;

export const MutedText = styled.p`
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.fontSize.xs};
`;

export const SectionHeading = styled.h3`
  font-size: ${theme.fontSize.md};
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text};
`;
